/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import {DataPath, Modem, ProvisionedTerminal} from '../../../shared/entities/provisionedTerminal';
import CustomDataGrid from '../../common/data_grid/CustomDataGrid';
import {GridActionsCellItem, GridColDef} from '@mui/x-data-grid';
import {TopAlignedChipCell} from '../../common/data_grid/ChipCell';
import React, {useCallback, useEffect, useState} from 'react';
import {Box, Button} from '@mui/material';
import appTheme from '../../common/theme/appTheme.json';
import AddIcon from '@mui/icons-material/Add';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ModalDialog from '../../common/dialogs/ModalDialog';
import {exportTerminalAsJson} from './exportUtils';
import WarningIcon from '@mui/icons-material/Warning';
import {Link, useRouteMatch} from 'react-router-dom';
import ErrorMessage from '../../common/layout/ErrorMessage';
import {StyledProvisioningPageHeading} from '../StyledProvisioningPageHeading';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import styled from 'styled-components';
import {RenderTopAlignedFixedHeightCell} from '../../common/data_grid/TopAlignedCell';
import {ajaxRaw} from '../../../utils/StoreUtils';

const DataGridWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: 100%;

  .MuiDataGrid-row--dynamicHeight {
    min-height: 49px !important;
  }
`;

interface TerminalsTableProps {
  disableVirtualization?: boolean;
}

const TerminalsTable = ({ disableVirtualization }: TerminalsTableProps) => {
  const { path } = useRouteMatch();
  const [deleteModalTarget, setDeleteModalTarget] = useState<ProvisionedTerminal | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [loadingTerminals, setLoadingTerminals] = useState(false);
  const [terminalsArray, setTerminalsArray] = useState<ProvisionedTerminal[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const loadTable = () => {
    setLoadingTerminals(true);
    ajaxRaw.get(`/provisioning/terminals`).subscribe({
      next: value => {
        setLoadingTerminals(false);
        setTerminalsArray(value.response);
      },
      error: err => {
        setLoadingTerminals(false);
        setError(err.message);
      },
    });
  };
  useEffect(loadTable, []);

  const COLLAPSED_ROW_HEIGHT_PX = 49;
  const columns: GridColDef[] = [
    {
      field: 'displayName',
      headerName: 'Terminal name',
      width: 200,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'customerGroup',
      headerName: 'Customer group',
      width: 160,
      valueGetter: params => params.value.toUpperCase(),
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'belongsToOrgs',
      headerName: 'Organization name(s)',
      width: 250,
      renderCell: params => <TopAlignedChipCell params={params} />,
      sortable: false,
    },
    {
      field: 'platformType',
      headerName: 'Platform type',
      width: 160,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'terminalType',
      headerName: 'Terminal type',
      width: 160,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
      renderCell: params => <TopAlignedChipCell params={params} />,
      sortable: false,
    },
    {
      // Note to devs: This field is hidden through the columnVisibilityModel prop in the DataGridWrapper. It's configured
      // here so that users can filter by modem identifiers.
      field: 'datapaths',

      // @ts-ignore This field will be replaced by getApplyQuickFilterFnV7 in the next release of MUI. According to MUI
      // docs for v6, passing a noop function is the correct way to configure this despite it causing a TS error.
      getApplyQuickFilterFn: () => {},

      // Custom filter function that allows users to filter by satellite IPs or MAC addresses that are nested within a
      // datapath's modems.
      getApplyQuickFilterFnV7: (filterValue) => (datapaths: DataPath[]) => {
        // Normalize a value to make it case-insensitive, remove "10.61." prefix from FTIs, and colons from MAC addresses
        // so that users may enter full satellite IPs or just the last octet, as well as MAC addresses with or without
        // colons.
        const normalizeValue = (value?: string | null) =>
            value?.toLowerCase().replace(/(:|10\.61\.)/g, '');

        filterValue = normalizeValue(filterValue);

        const filterModemIdentifiers = (modem: Modem) => {
          return normalizeValue(modem.satIp)?.includes(filterValue) ||
                 normalizeValue(modem.macAddress)?.includes(filterValue);
        }

        return (datapaths ?? []).some(
            datapath => datapath.modems.some(filterModemIdentifiers)
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      width: 140,
      cellClassName: 'ActionsCell-TopAligned',
      getActions: params => [
        <GridActionsCellItem
          key={'create'}
          icon={<CreateIcon />}
          component={Link}
          to={`${path}/edit/${params.row.id}`}
        />,
        <GridActionsCellItem
          key={'export'}
          icon={<FileDownloadIcon />}
          label="Export JSON"
          onClick={() => exportTerminalAsJson(params.row)}
        />,
        <GridActionsCellItem
          key={'delete'}
          icon={<DeleteIcon />}
          onClick={() => {
            setDeleteModalTarget(params.row);
            setShowDeleteModal(true);
          }}
        />,
      ],
    },
  ];

  const AddTerminalButton = useCallback(
    () => (
      <Button
        component={Link}
        to={`${path}/new`}
        startIcon={<AddIcon />}
        variant="contained"
        sx={{
          marginLeft: 'auto',
          color: '#000000',
          letterSpacing: '0.8px',
          backgroundColor: appTheme.mptLightBlue,
          '&:hover': {
            backgroundColor: appTheme.mptLightBlue,
            svg: {
              color: '#000000',
            },
          },
        }}
      >
        Provision new terminal
      </Button>
    ),
    [path],
  );

  return (
    <>
      {error ? <ErrorMessage>{error}</ErrorMessage> : null}

      <StyledProvisioningPageHeading>
        Terminals {terminalsArray ? `(${terminalsArray.length})` : ''}
      </StyledProvisioningPageHeading>

      {loadingTerminals ? <ModalLoadingSpinner /> : null}

      <DataGridWrapper>
        <CustomDataGrid
          disableVirtualization={disableVirtualization}
          columns={columns}
          rows={terminalsArray || []}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => COLLAPSED_ROW_HEIGHT_PX}
          initialState={{
            sorting: {
              sortModel: [{ field: 'displayName', sort: 'asc' }],
            },
          }}
          toolbarActionButtons={[<AddTerminalButton key={'add-button'} />]}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: 'Terminals',
              },
            },
            filterPanel: {
              filterFormProps: {
                // Hide the "datapaths" column from the filter form's column dropdown
                filterColumns: ({columns}) =>
                    columns.map(col => col.field)
                           .filter(col => col !== 'datapaths'),
              }
            },
          }}
          columnVisibilityModel={{
            // datapaths is only used for filtering, so it should be hidden by default
            datapaths: false
          }}
        />
      </DataGridWrapper>

      <ModalDialog
        open={showDeleteModal}
        onLeftBtnClicked={() => {
          setShowDeleteModal(false);
        }}
        onRightBtnClicked={() => {
          ajaxRaw.delete(`/provisioning/terminals/${deleteModalTarget!.id}`).subscribe({
            next: value => {
              loadTable();
              setShowDeleteModal(false);
            },
            error: err => {
              loadTable();
              setError(err);
              setShowDeleteModal(false);
            },
          });
        }}
        onExited={() => {
          // Remove the delete target after the dialog's exit animation has finished. Otherwise, the `message` below
          // will briefly display `undefined` for the terminal's display name
          setDeleteModalTarget(undefined);
        }}
        title={
          <Box display={'flex'} gap={'8px'} alignItems={'center'}>
            <WarningIcon htmlColor={'#FFB029'} /> Confirm Terminal Deletion
          </Box>
        }
        message={
          <>
            Are you sure you want to delete <code>{deleteModalTarget?.displayName}</code>?
            <br />
            <br />
            This action cannot be undone.
          </>
        }
      />
    </>
  );
};

export default TerminalsTable;
